import { useState } from 'react';

import { Loader2, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useScenarioList } from '@/hooks/useScenarios';
import { REPLAY_RESULT_VARIANT } from '@/lib/api/replays.constants';
import { formatDateTime } from '@/utils/format';

import { ScenarioDraftDialog } from './components/ScenarioDraftDialog';

import type { Scenario } from '@/lib/api/scenarios.api';

const LastResultBadge = ({ scenario }: { scenario: Scenario }) => {
  const result = scenario.last_result;
  if (!result) return <span className="text-muted-foreground">—</span>;
  return <Badge variant={REPLAY_RESULT_VARIANT[result]}>{result}</Badge>;
};

export const ScenariosListPage = () => {
  usePageTitle('Scenarios');
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [draftOpen, setDraftOpen] = useState(false);
  const { data: scenarios, isLoading } = useScenarioList();

  const needle = search.trim().toLowerCase();
  const visible = (scenarios ?? []).filter(
    (s) =>
      !needle ||
      s.name.toLowerCase().includes(needle) ||
      s.tags.some((t) => t.toLowerCase().includes(needle)),
  );

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle>Scenarios</CardTitle>
            <CardDescription>
              Reusable test conversations: a customer brief plus the checks and rules the agent must
              satisfy. Assign them to configurations and channels, then run them as a suite.
            </CardDescription>
          </div>
          <Button onClick={() => setDraftOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> New scenario
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by name or tag"
            className="max-w-sm"
          />

          {!scenarios?.length ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              No scenarios yet. Create one to start testing the agent against a script.
            </p>
          ) : visible.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              No scenarios match &quot;{search}&quot;.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>Assignments</TableHead>
                  <TableHead>Last result</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((s) => (
                  <TableRow
                    key={s.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/agent-improvement/scenarios/${s.id}`)}
                  >
                    <TableCell className="font-medium">
                      <div>{s.name}</div>
                      {s.description && (
                        <div className="text-muted-foreground line-clamp-1 max-w-md text-xs font-normal">
                          {s.description}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {s.tags.length ? (
                        <div className="flex flex-wrap gap-1">
                          {s.tags.map((t) => (
                            <Badge key={t} variant="secondary" className="text-xs">
                              {t}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{s.assignments_count ?? '—'}</TableCell>
                    <TableCell>
                      <LastResultBadge scenario={s} />
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {formatDateTime(s.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ScenarioDraftDialog open={draftOpen} onOpenChange={setDraftOpen} />
    </div>
  );
};
